import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight } from 'lucide-react';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { ScrollController } from '@/components/ScrollController';
import { SimpleReveal } from '@/components/SimpleReveal';
import { Button } from '@/components/ui/button';
import { caseStudiesPageData } from '@/data/caseStudiesPageData';

import brianStats from '@/assets/brian-michael-spotify-stats.png';

const brian = caseStudiesPageData[0];

const BriansStory = () => {
  return (
    <>
      <Helmet>
        <title>Brian's Story | From Zero to 500K Streams</title>
        <meta
          name="description"
          content="How Brian Michael went from zero audience to 500K+ Spotify streams and a development deal in LA. A real artist growth story."
        />
      </Helmet>

      <div className="min-h-screen bg-[#0a0a0a]">
        <Navigation />
        <ScrollController>
          {/* Header */}
          <section className="pt-32 lg:pt-40 pb-8 lg:pb-12 relative" data-nav-theme="dark">
            <div
              className="absolute inset-0 grid-pattern pointer-events-none"
              style={{
                maskImage: 'radial-gradient(ellipse 100% 80% at 50% 30%, black 0%, rgba(0,0,0,0.4) 50%, transparent 85%)',
                WebkitMaskImage: 'radial-gradient(ellipse 100% 80% at 50% 30%, black 0%, rgba(0,0,0,0.4) 50%, transparent 85%)',
              }}
            />
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] lg:w-[900px] lg:h-[500px] rounded-full pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse at center, rgba(0,113,227,0.12) 0%, rgba(0,113,227,0.05) 40%, transparent 70%)',
                filter: 'blur(60px)',
              }}
            />

            <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
              <SimpleReveal delay={60}>
                <div className="flex justify-center mb-4">
                  <div className="inline-flex items-center gap-2 px-2 py-1 md:px-3 md:py-1.5 bg-[#0071e3]/10 border border-[#0071e3]/25 rounded-md transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-[#0071e3]/15 hover:border-[#0071e3]/35 hover:shadow-[0_0_20px_rgba(0,113,227,0.3)]">
                    <span className="font-mono text-xs font-medium text-[#0071e3] tracking-wider md:tracking-[0.15em] uppercase">
                      &gt; Success Story
                    </span>
                  </div>
                </div>
              </SimpleReveal>

              <SimpleReveal delay={120}>
                <h1 className="headline-lg text-white mb-4">
                  How Brian Got His First 500K Streams Starting{' '}
                  <span className="inline-block bg-[#f5f5f7] text-[#1d1d1f] px-3 py-1 rounded-lg font-bold -rotate-2 shadow-[0_0_40px_rgba(0,113,227,0.7)]">
                    From Zero
                  </span>
                </h1>
              </SimpleReveal>
            </div>
          </section>

          {/* Video + Story + Stats */}
          <section className="pb-16 lg:pb-20 relative" data-nav-theme="dark">
            <div className="max-w-6xl mx-auto px-6">
              <SimpleReveal delay={200}>
                <div className="flex justify-center">
                  <div className="max-w-md w-full">
                    <div className="relative w-full aspect-[9/16] rounded-xl overflow-hidden bg-black border border-white/[0.08]">
                      <iframe
                        src={`https://player.vimeo.com/video/${brian.vimeoId}?autoplay=1&muted=1&loop=1&title=0&byline=0&portrait=0`}
                        className="w-full h-full"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </div>
                </div>

                {/* Story Text - Mobile Only */}
                <div className="lg:hidden mt-8 space-y-4">
                  <p className="text-sm text-white/70 leading-relaxed">
                    Brian came to us with zero audience, zero streams, and a dream to make country music his full-time career. We built his brand from scratch with a content strategy centered on authentic storytelling.
                  </p>
                  <p className="text-sm text-white/70 leading-relaxed">
                    His single 'Seeing Mama Cry' became the perfect vehicle — a deeply personal song that connected emotionally with listeners. Within months, Brian went from complete unknown to over 500K streams on a single track.
                  </p>
                  <p className="text-sm text-white/70 leading-relaxed">
                    The content series we created built such strong fan loyalty that industry executives took notice. Shortly after, Brian secured a development deal in LA. Proof that our system doesn't just build numbers — it builds careers.
                  </p>
                </div>

                {/* Spotify Stats Image */}
                <div className="mt-10 lg:mt-14 max-w-2xl mx-auto">
                  <img
                    src={brianStats}
                    alt="Brian Michael Spotify analytics"
                    className="w-full rounded-xl border border-white/[0.08] transition-all duration-300 hover:-translate-y-2 hover:scale-[1.02] hover:shadow-[0_20px_40px_-10px_rgba(0,0,0,0.4)] transform-gpu"
                    loading="lazy"
                  />
                </div>
              </SimpleReveal>
            </div>
          </section>

          {/* CTA Section */}
          <section className="pb-20 lg:pb-28 relative" data-nav-theme="dark">
            <div className="max-w-3xl mx-auto px-6 text-center">
              <SimpleReveal delay={100}>
                <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3">
                  We Are Taking Applications For Starting With Us In October
                </h2>
                <p className="text-white/50 mb-8 text-base lg:text-lg">
                  Apply now and see what's possible for your music.
                </p>

                <div className="flex justify-center">
                  <div className="rotating-border-button">
                    <a
                      href="https://a23j5fry30t.typeform.com/to/CzjaFT5z"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        variant="hero-black-ring"
                        size="hero"
                        className="group relative z-10"
                      >
                        Apply Now
                        <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                      </Button>
                    </a>
                  </div>
                </div>
              </SimpleReveal>
            </div>
          </section>
        </ScrollController>
        <Footer />
      </div>
    </>
  );
};

export default BriansStory;
